import { Button, FormControl, Grid, IconButton, Menu, MenuItem, Radio, Select } from "@mui/material";
import { ActionTypes, MatchingConditionOperator } from "../../constants/common";
import { useState } from "react";
import { DataViewCondition } from "../../types/dataViews";
import { Add, ArrowDropDown, RemoveCircleOutline } from "@mui/icons-material";
import InputTextBox from "../Common/InputTextBox";
import { RunInstanceState, RunInstanceStateToName } from "../../constants/workflow";
import clsx from "clsx";
import { WorkflowIcon } from "../../svgUrls";
import { lightGreyWithSmallIconCss } from "../../constants/selectCSS";
import { FallbackSvg } from '../Common/FallbackSvg';
import { WorkflowServiceIcon } from "../Common/WorkflowServiceIcon";
type DataViewsFiltersProps = {
    overallFilterOperator: MatchingConditionOperator;
    setOverallFilterOperator: (op: MatchingConditionOperator) => void;
    columnsDetails: {
        field_name: string;
        node_id: string | undefined;
        service: string;
    }[];
    closeFilters: () => void;
    emptyCondition: DataViewCondition;
    conditions: DataViewCondition[];
    setConditions: (conditions: DataViewCondition[]) => void;
    applyFilters: () => void;
}

const DataViewsFilters = ({
    overallFilterOperator,
    setOverallFilterOperator,
    columnsDetails,
    closeFilters,
    emptyCondition,
    conditions,
    setConditions,
    applyFilters,
}: DataViewsFiltersProps) => {
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    return (
        <div className="p-3 text-xs w-[700px] max-h-[80%] overflow-auto">
            <div>Filter this view to show records where...</div>
            <div className="mt-2 border border-solid border-borderGrey rounded p-3 text-greyishBlue">
                <div className="text-greyishBlue">{overallFilterOperator === MatchingConditionOperator.AND ? 'All' : 'Any'} of the following are true</div>
                <Grid
                    key={'headers'}
                    container
                    alignItems="center"
                    spacing={1}
                    className="!mt-2 mb-1 !grid grid-cols-[55px_calc(35%_-24px)_calc(30%_-24px)_calc(35%_-24px)_21px]"
                >
                    <Grid item></Grid>
                    <Grid item className="text-greyishBlueLight text-3xs">FIELD</Grid>
                    <Grid item className="text-greyishBlueLight text-3xs">OPERATOR</Grid>
                    <Grid item className="text-greyishBlueLight text-3xs">VALUE</Grid>
                    <Grid item></Grid>
                </Grid>
                {
                    conditions.map((condition, index) => {
                        return (
                            <Grid
                                key={index}
                                container
                                alignItems="center"
                                spacing={1}
                                className={clsx("!grid grid-cols-[55px_calc(35%_-24px)_calc(30%_-24px)_calc(35%_-24px)_21px]", { "!mt-2": index !== 0 })}
                            >
                                <Grid item>
                                    <div className={clsx("flex justify-between items-center gap-1 text-xs px-1 rounded capitalize py-2", index === 0 ? "text-greyishBlueLight bg-backgroundGrey" : index === 1 ? "text-black border border-solid border-borderGrey" : "")}>
                                    <div>
                                        {index === 0 ? 'Where' : overallFilterOperator}
                                    </div>
                                        <IconButton onClick={(e) => setMenuAnchorEl(e.currentTarget)} className="!p-0">
                                            {index === 1 ? (
                                                <ArrowDropDown
                                                    fontSize="small"
                                                    className="cursor-pointer !text-sm text-lightSlateGrey"
                                                />
                                            ) : (
                                                <div className="w-5"></div>
                                            )}
                                        </IconButton>
                                    </div>
                                </Grid>
                                <Grid item>
                                    <FormControl variant="outlined" size="small" fullWidth>
                                        <Select
                                            className="!text-xs"
                                            value={condition.field_name === "state" ? "Run status" : condition.field_name}
                                            renderValue={() => {
                                            return (
                                                <div className="truncate">
                                                    {condition.field_name === "state" ? "Run status" : condition.field_name}
                                                </div>
                                            );
                                            }}
                                            MenuProps={{
                                                anchorOrigin: {
                                                    vertical: 'bottom',
                                                    horizontal: 'left',
                                                },
                                                transformOrigin: {
                                                    vertical: 'top',
                                                    horizontal: 'left',
                                                },
                                            }}
                                            onChange={(e) => {
                                                const _conditions = [...conditions];
                                                const colName = e.target.value?.split("|")?.[1] || "";
                                                const nodeId = e.target.value?.split("|")?.[0];
                                                _conditions[index].field_name = colName;
                                                if(nodeId)
                                                    _conditions[index].node_id = nodeId;
                                                else
                                                   delete( _conditions[index].node_id);
                                                _conditions[index].value = "";
                                                setConditions(_conditions);
                                            }}
                                            sx={{
                                                ...lightGreyWithSmallIconCss
                                            }}
                                        >
                                            {columnsDetails.map((column) => {
                                                if(column.field_name === "Last step run") {
                                                    return null;
                                                }
                                                return <MenuItem key={column.node_id+"|"+column.field_name}
                                                    value={(column.node_id || "")+"|"+(column.field_name === "Run status" ? 'state' : column.field_name)}
                                                    className="flex items-center gap-2"
                                                >
                                                    {column.service === "none" ?
                                                        <WorkflowIcon />
                                                        : <WorkflowServiceIcon
                                                            workflowType={column.service}
                                                            alt={column.service}
                                                            height={20}
                                                            width={20}
                                                        />
                                                    }
                                                    <div className="text-xs">{column.field_name}</div>
                                                </MenuItem>
                                            })}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item>
                                    <FormControl variant="outlined" size="small" fullWidth>
                                        <Select
                                            className="!text-xs"
                                            value={condition.match_type}
                                            renderValue={() => {
                                                return (
                                                    <div className="truncate">
                                                        {condition.match_type}
                                                    </div>
                                                );
                                            }}
                                            MenuProps={{
                                                anchorOrigin: {
                                                    vertical: 'bottom',
                                                    horizontal: 'left',
                                                },
                                                transformOrigin: {
                                                    vertical: 'top',
                                                    horizontal: 'left',
                                                },
                                            }}
                                            onChange={(e) => {
                                                const _conditions = [...conditions];
                                                _conditions[index].match_type = e.target.value as ActionTypes;
                                                setConditions(_conditions);
                                            }}
                                            sx={{
                                                ...lightGreyWithSmallIconCss
                                            }}
                                        >
                                            {Object.values(ActionTypes).map((action) => (
                                                <MenuItem key={action} value={action}>
                                                    <div className="text-xs">{action}</div>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item>
                                    {
                                        condition.field_name === "state" ?
                                        <Select
                                            className="!text-xs"
                                            fullWidth
                                            size="small"
                                            value={condition.field_name === "state" ? RunInstanceStateToName[condition.value as unknown as RunInstanceState] : condition.value}
                                            renderValue={() => {
                                                return (
                                                    <div className="truncate">
                                                        {condition.field_name === "state" ? RunInstanceStateToName[condition.value as unknown as RunInstanceState] : condition.value}
                                                    </div>
                                                );
                                            }}
                                            MenuProps={{
                                                anchorOrigin: {
                                                    vertical: 'bottom',
                                                    horizontal: 'left',
                                                },
                                                transformOrigin: {
                                                    vertical: 'top',
                                                    horizontal: 'left',
                                                },
                                            }}
                                            onChange={(e) => {
                                                const _conditions = [...conditions];
                                                _conditions[index].value = e.target.value;
                                                setConditions(_conditions);
                                            }}
                                            sx={{
                                                ...lightGreyWithSmallIconCss
                                            }}
                                        >
                                            {Object.entries(RunInstanceStateToName).map(([key, value]) => (
                                                <MenuItem key={key} value={key}>
                                                    <div className="text-xs">{value}</div>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        : <InputTextBox
                                            initialValue={condition.value}
                                            size="small"
                                            setTextValue={(text) => {
                                                const _conditions = [...conditions];
                                                _conditions[index].value = text;
                                                setConditions(_conditions);
                                            }}
                                            borderColor="#EBECF0"
                                            borderRadius="4px"
                                        />
                                    }
                                </Grid>
                                <Grid item>
                                    <RemoveCircleOutline
                                        htmlColor="#94A1AF"
                                        className="cursor-pointer !text-sm"
                                        onClick={() => {
                                            const _conditions = [...conditions];
                                            _conditions.splice(index, 1);
                                            setConditions(_conditions);
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        )
                    })
                }

                <Button
                    variant="outlined"
                    className="!mt-4 !text-nanonetsBlue !border-[#d9dfff] !normal-case"
                    startIcon={<Add className="!text-sm !text-nanonetsBlue" />}
                    size="small"
                    onClick={() => {
                        const _conditions = [...conditions];
                        _conditions.push({...emptyCondition});
                        setConditions([..._conditions]);
                    }}
                >
                    Add condition
                </Button>
            </div>

            <div className="flex justify-end mt-4 gap-4">
                <Button variant="outlined" className="!text-greyishBlue !border-borderGrey !normal-case" size="small"
                    onClick={closeFilters}
                >
                    Cancel
                </Button>
                <Button variant="contained" className="!bg-nanonetsBlue !normal-case" size="small"
                    onClick={() => {
                        closeFilters();
                        setTimeout(() => {
                            applyFilters();
                        }, 600);
                    }}
                >
                    Apply filters
                </Button>
            </div>

            <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <MenuItem
                    onClick={() => {
                        setOverallFilterOperator(MatchingConditionOperator.AND);
                        handleMenuClose();
                    }}
                >
                    <Radio
                        color="primary"
                        size="small"
                        checked={
                            overallFilterOperator === MatchingConditionOperator.AND
                        }
                    />
                    <div className="text-xs">AND</div>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        setOverallFilterOperator(MatchingConditionOperator.OR);
                        handleMenuClose();
                    }}
                >
                    <Radio
                    color="primary"
                    size="small"
                    checked={
                        overallFilterOperator === MatchingConditionOperator.OR
                    }
                    />
                    <div className="text-xs">OR</div>
                </MenuItem>
            </Menu>
        </div>
    )
}

export default DataViewsFilters;